/**
 * Color Family Bar — Two-Step Morph
 *
 * Single row that morphs between two layers:
 *   Layer 1: family pills (Pink, Yellow, Blue, ...)
 *   Layer 2: child swatches of the chosen family + back arrow
 *
 * On child click:
 *   1. Instant local UI update (selected state applies immediately, ~80ms transition)
 *   2. Toggle matching checkbox in 产品系列 section's filter form
 *   3. Dispatch change event → theme's facet-inputs-component handles AJAX
 *
 * External state sync (e.g. user removes a chip × in native filters):
 *   - Listens for change events on document with self-trigger guard
 *   - Polls URL every 250ms as a fallback (catches pushState/replaceState)
 *   - popstate handler for browser back/forward
 */

const FILTER_NAME = 'filter.v.t.shopify.color-pattern';

class ColorFamilyBar extends HTMLElement {
  constructor() {
    super();
    this._currentFamily = null;
    this._isTogglingSelf = false;
    this._lastUrl = '';
    this._urlPollInterval = null;
    this._prefetchedValues = new Set();
    this._linkPrefetched = new Set();

    this._onClick = this._onClick.bind(this);
    this._onKey = this._onKey.bind(this);
    this._onPopState = this._onPopState.bind(this);
    this._onDocChange = this._onDocChange.bind(this);
    this._checkUrlChange = this._checkUrlChange.bind(this);
    this._onPointerOver = this._onPointerOver.bind(this);
    this._onPointerOut = this._onPointerOut.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onLayerScroll = this._onLayerScroll.bind(this);
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
    document.addEventListener('keydown', this._onKey);
    window.addEventListener('popstate', this._onPopState);

    // Listen for color-filter checkbox changes from anywhere (chip × removals,
    // direct native filter toggles, etc.)
    document.addEventListener('change', this._onDocChange, true);

    // ===== Prefetch on hover / touch =====
    // Triggers theme's native prefetch mechanism (Horizon does
    // `on:pointerenter="/prefetchPage"` on filter inputs) so the filtered
    // page response is warm in browser cache by the time user clicks.
    this.addEventListener('pointerover', this._onPointerOver);
    this.addEventListener('pointerout', this._onPointerOut);
    this.addEventListener('touchstart', this._onTouchStart, { passive: true });

    // URL polling as a universal fallback for pushState-based filter changes
    this._lastUrl = window.location.href;
    this._urlPollInterval = setInterval(this._checkUrlChange, 250);

    // Scroll-hint listeners on each layer (mobile right-edge fade)
    this.querySelectorAll('.cfm-layer').forEach((layer) => {
      layer.addEventListener('scroll', this._onLayerScroll, { passive: true });
    });
    window.addEventListener('resize', this._onResize);

    // Sync UI from current state on mount
    this._syncFromUrl();
    // Compute initial scroll-hint state (run after layout settles)
    requestAnimationFrame(() => this._updateScrollHints());

    // Debug overlay — activate by adding ?cfm-debug=1 to the URL.
    // Shows real-time state of every family layer in a floating panel.
    if (new URLSearchParams(window.location.search).has('cfm-debug')) {
      this._initDebugOverlay();
    }
  }

  _initDebugOverlay() {
    const panel = document.createElement('div');
    panel.style.cssText = [
      'position:fixed', 'bottom:12px', 'left:12px', 'right:12px',
      'max-height:60vh', 'overflow:auto',
      'background:rgba(0,0,0,0.92)', 'color:#0f0',
      'font:11px/1.5 ui-monospace,Menlo,Consolas,monospace',
      'padding:10px 12px', 'border-radius:6px',
      'z-index:999999', 'box-shadow:0 4px 20px rgba(0,0,0,0.4)',
      'white-space:pre-wrap', 'word-break:break-all',
      'pointer-events:auto',
    ].join(';');
    panel.id = 'cfm-debug-panel';

    const close = document.createElement('button');
    close.textContent = '×';
    close.style.cssText = 'position:absolute;top:4px;right:8px;background:none;border:none;color:#fff;font-size:18px;cursor:pointer';
    close.onclick = () => panel.remove();
    panel.appendChild(close);

    const content = document.createElement('div');
    panel.appendChild(content);
    document.body.appendChild(panel);

    const render = () => {
      const form = this.getForm();
      const colorBoxes = form
        ? form.querySelectorAll(`input[name="${FILTER_NAME}"]`).length
        : 0;
      const colorChecked = form
        ? form.querySelectorAll(`input[name="${FILTER_NAME}"]:checked`).length
        : 0;

      const lines = [];
      lines.push(`UA: ${navigator.userAgent.slice(0, 80)}`);
      lines.push(`Viewport: ${window.innerWidth}×${window.innerHeight}`);
      lines.push(`Form: ${form ? form.id : 'NOT FOUND'}`);
      lines.push(`Color checkboxes in form: ${colorBoxes} (checked: ${colorChecked})`);
      lines.push(`URL: ${window.location.search || '(none)'}`);
      lines.push('');
      lines.push('FAMILIES (handle / DOM child count / dataset value / selected / visible):');

      this.querySelectorAll('.cfm-layer-children').forEach((l) => {
        const handle = l.dataset.cfmFamilyLayer || '?';
        const domCount = l.querySelectorAll('.cfm-child').length;
        const datasetVal = l.dataset.cfmTotalChildren || '(empty)';
        const selected = l.querySelectorAll('.cfm-child.is-selected').length;
        const visible = !l.hidden;
        const status = domCount === 0 ? ' ⚠️ NO CHILDREN' : '';
        lines.push(`  ${handle.padEnd(12)} dom=${domCount}  ds=${datasetVal}  sel=${selected}  vis=${visible}${status}`);
      });

      // Show first few cfm-child values to help spot GID mismatches
      const firstChild = this.querySelector('.cfm-child');
      if (firstChild) {
        lines.push('');
        lines.push(`Sample cfm-child value: ${firstChild.dataset.cfmValue || '(empty)'}`);
      }

      // Sample what's in the form
      if (form) {
        const sampleBox = form.querySelector(`input[name="${FILTER_NAME}"]`);
        if (sampleBox) {
          lines.push(`Sample form value:     ${sampleBox.value}`);
        }
      }

      content.textContent = lines.join('\n');
    };

    render();
    setInterval(render, 800);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('popstate', this._onPopState);
    document.removeEventListener('change', this._onDocChange, true);
    window.removeEventListener('resize', this._onResize);
    if (this._urlPollInterval) clearInterval(this._urlPollInterval);
    this.querySelectorAll('.cfm-layer').forEach((layer) => {
      layer.removeEventListener('scroll', this._onLayerScroll);
    });
  }

  // ============================================================
  // Mobile scroll hint — toggles .can-scroll-left/.can-scroll-right
  // classes on each layer so CSS can show the right-edge fade and
  // animated chevron only when there's actually more content to scroll.
  // ============================================================

  _onLayerScroll(e) {
    this._updateScrollHintFor(e.currentTarget);
  }

  _onResize() {
    this._updateScrollHints();
  }

  _updateScrollHints() {
    this.querySelectorAll('.cfm-layer').forEach(layer => this._updateScrollHintFor(layer));
  }

  _updateScrollHintFor(layer) {
    if (!layer) return;
    const canRight = layer.scrollLeft + layer.clientWidth < layer.scrollWidth - 2;
    const canLeft = layer.scrollLeft > 2;
    layer.classList.toggle('can-scroll-right', canRight);
    layer.classList.toggle('can-scroll-left', canLeft);
    // Show chevron only when the currently visible layer can scroll right
    if (layer.classList.contains('is-active') || layer.classList.contains('cfm-layer-families')) {
      this.classList.toggle('can-scroll-hint', canRight);
    }
  }

  // ============================================================
  // Form access — find the live filter form in 产品系列 section
  // ============================================================

  getForm() {
    const candidates = document.querySelectorAll('form[id^="FacetFiltersForm--"]');
    for (const form of candidates) {
      if (form.querySelector(`input[name="${FILTER_NAME}"]`)) return form;
    }
    return candidates[0] || null;
  }

  findCheckbox(value) {
    const form = this.getForm();
    if (!form) return null;
    return form.querySelector(
      `input[type="checkbox"][name="${FILTER_NAME}"][value="${CSS.escape(value)}"]`
    );
  }

  getAllColorCheckboxes() {
    return Array.from(
      document.querySelectorAll(
        `form[id^="FacetFiltersForm--"] input[type="checkbox"][name="${FILTER_NAME}"]`
      )
    );
  }

  // ============================================================
  // Event handlers
  // ============================================================

  _onClick(e) {
    const target = e.target.closest('[data-cfm-action]');
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();

    const action = target.dataset.cfmAction;
    switch (action) {
      case 'open-family':
        this._openFamily(target.dataset.cfmFamily);
        break;
      case 'back':
        this._goBack();
        break;
      case 'toggle-child':
        this._toggleChild(target);
        break;
      case 'clear-all':
        this._clearAll();
        break;
    }
  }

  _onKey(e) {
    if (e.key === 'Escape' && this._currentFamily) {
      this._goBack();
    }
  }

  _onPopState() {
    this._syncFromUrl();
    this._goBack();
  }

  /** Color filter checkbox change from anywhere else on the page. */
  _onDocChange(e) {
    if (this._isTogglingSelf) return; // skip self-triggered events
    const t = e.target;
    if (!t || t.tagName !== 'INPUT') return;
    if (t.type !== 'checkbox') return;
    if (t.name !== FILTER_NAME) return;
    // External change (e.g. chip × removal, native filter toggle) —
    // sync our UI from form state + show skeleton loading
    this._syncFromForm();
    this._showLoading();
  }

  _checkUrlChange() {
    const currentUrl = window.location.href;
    if (currentUrl !== this._lastUrl) {
      this._lastUrl = currentUrl;
      this._syncFromUrl();
      // URL change = AJAX completed → remove skeleton loading state
      this._hideLoading();
      // Reset prefetch cache when URL changes — old prefetches may be stale
      this._prefetchedValues.clear();
    }
  }

  // ============================================================
  // Skeleton loading state (Azazie-style gray product placeholders)
  // ============================================================

  _showLoading() {
    document.documentElement.classList.add('cfm-products-loading');
    // Failsafe: clear loading state after 6s in case URL never changes
    // (e.g. network error, theme issue)
    clearTimeout(this._loadingTimer);
    this._loadingTimer = setTimeout(() => this._hideLoading(), 6000);
  }

  _hideLoading() {
    document.documentElement.classList.remove('cfm-products-loading');
    clearTimeout(this._loadingTimer);
    // Clear per-pill spinners
    this.querySelectorAll('.cfm-child.is-loading').forEach(el =>
      el.classList.remove('is-loading'));
  }

  // ============================================================
  // Prefetch on hover / touch
  // ============================================================

  _onPointerOver(e) {
    const btn = e.target.closest('[data-cfm-action="toggle-child"]');
    if (!btn) return;
    // Skip if moving within the same button (internal pointer movement)
    if (e.relatedTarget && btn.contains(e.relatedTarget)) return;
    this._prefetchForChild(btn);
  }

  _onPointerOut(e) {
    const btn = e.target.closest('[data-cfm-action="toggle-child"]');
    if (!btn) return;
    if (e.relatedTarget && btn.contains(e.relatedTarget)) return;
    this._cancelPrefetchForChild(btn);
  }

  _onTouchStart(e) {
    const btn = e.target.closest('[data-cfm-action="toggle-child"]');
    if (btn) this._prefetchForChild(btn);
  }

  /**
   * Trigger the theme's native page prefetch by:
   *   (a) dispatching `pointerenter` on the matching form input/label, so
   *       Horizon's `on:pointerenter="/prefetchPage"` handler fires;
   *   (b) as a fallback, adding a `<link rel="prefetch">` for the URL
   *       that would result from this filter change.
   */
  _prefetchForChild(button) {
    const value = button.dataset.cfmValue;
    if (!value) return;
    if (this._prefetchedValues.has(value)) return;
    this._prefetchedValues.add(value);

    // ----- (a) Trigger theme's own prefetch -----
    const checkbox = this.findCheckbox(value);
    if (checkbox) {
      const targets = [
        checkbox.closest('label'),
        checkbox.closest('fieldset'),
        checkbox.parentElement,
        checkbox,
      ].filter(Boolean);
      const evt = new PointerEvent('pointerenter', {
        bubbles: true,
        cancelable: true,
        pointerType: 'mouse',
      });
      targets.forEach(el => { try { el.dispatchEvent(evt); } catch (_) {} });
    }

    // ----- (b) Fallback: <link rel="prefetch"> the resulting URL -----
    try {
      const willBeActive = !button.classList.contains('is-selected');
      const prefetchUrl = this._buildResultUrl(value, willBeActive);
      if (prefetchUrl && !this._linkPrefetched.has(prefetchUrl)) {
        this._linkPrefetched.add(prefetchUrl);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = prefetchUrl;
        link.as = 'document';
        document.head.appendChild(link);
      }
    } catch (_) { /* prefetch is best-effort */ }
  }

  _cancelPrefetchForChild(button) {
    const value = button.dataset.cfmValue;
    if (!value) return;
    const checkbox = this.findCheckbox(value);
    if (!checkbox) return;
    const targets = [
      checkbox.closest('label'),
      checkbox.closest('fieldset'),
      checkbox.parentElement,
      checkbox,
    ].filter(Boolean);
    const evt = new PointerEvent('pointerleave', {
      bubbles: true,
      cancelable: true,
      pointerType: 'mouse',
    });
    targets.forEach(el => { try { el.dispatchEvent(evt); } catch (_) {} });
  }

  /** Build the URL that would result if user toggled this value. */
  _buildResultUrl(value, willBeActive) {
    const u = new URL(window.location.href);
    const params = u.searchParams;
    const current = params.getAll(FILTER_NAME);

    let updated;
    if (willBeActive) {
      updated = current.includes(value) ? current : [...current, value];
    } else {
      updated = current.filter(v => v !== value);
    }

    params.delete(FILTER_NAME);
    updated.forEach(v => params.append(FILTER_NAME, v));
    return u.toString();
  }

  // ============================================================
  // Morph transitions
  // ============================================================

  _openFamily(handle) {
    if (!handle) return;
    const layer = this.querySelector(
      `[data-cfm-family-layer="${CSS.escape(handle)}"]`
    );
    if (!layer) return;

    const currentActive = this.querySelector('.cfm-layer-children.is-active');
    if (currentActive && currentActive !== layer) {
      currentActive.classList.remove('is-active');
      this._hideAfterTransition(currentActive);
    }

    layer.hidden = false;
    void layer.offsetWidth; // force reflow so transition plays
    layer.classList.add('is-active');

    this.classList.add('is-expanded');
    this._currentFamily = handle;

    // Recompute scroll hint for the now-active children layer
    requestAnimationFrame(() => this._updateScrollHintFor(layer));
  }

  _goBack() {
    if (!this._currentFamily) return;
    const layer = this.querySelector(
      `[data-cfm-family-layer="${CSS.escape(this._currentFamily)}"]`
    );
    if (layer) {
      layer.classList.remove('is-active');
      this._hideAfterTransition(layer);
    }
    this.classList.remove('is-expanded');
    this._currentFamily = null;

    // Recompute scroll hint based on the now-visible families layer
    requestAnimationFrame(() => {
      const familiesLayer = this.querySelector('.cfm-layer-families');
      if (familiesLayer) this._updateScrollHintFor(familiesLayer);
    });
  }

  _hideAfterTransition(layer) {
    const onEnd = (e) => {
      if (e && e.target !== layer) return;
      if (!layer.classList.contains('is-active')) {
        layer.hidden = true;
      }
      layer.removeEventListener('transitionend', onEnd);
    };
    layer.addEventListener('transitionend', onEnd);
    setTimeout(() => {
      if (!layer.classList.contains('is-active')) layer.hidden = true;
    }, 600);
  }

  // ============================================================
  // Filter mutations — drive AJAX via real form checkboxes
  // ============================================================

  _toggleChild(button) {
    const value = button.dataset.cfmValue;
    const checkbox = this.findCheckbox(value);

    if (!checkbox) {
      console.warn(
        '[color-family-bar] No checkbox found for value:', value,
        '— check that S&D Color filter is enabled and metaobject child_handles holds valid GIDs.'
      );
      return;
    }

    const willBeActive = !button.classList.contains('is-selected');

    // 1) Instant local UI update — feels immediate to the user
    button.classList.toggle('is-selected', willBeActive);
    button.classList.add('is-loading');   // spinner ring on the dot
    const layer = button.closest('.cfm-layer-children');
    if (layer) {
      this._updateLayerMeta(layer);
      this._updateFamilyPill(layer.dataset.cfmFamilyLayer);
    }
    this._updateAllPill();

    // 2) Show skeleton loading on product grid immediately
    this._showLoading();

    // 3) Toggle live checkbox + trigger AJAX (with self-flag so we don't
    //    bounce-back on our own change event)
    this._isTogglingSelf = true;
    checkbox.checked = willBeActive;
    this._dispatchChange(checkbox);
    setTimeout(() => { this._isTogglingSelf = false; }, 0);
  }

  _clearAll() {
    const checked = this.getAllColorCheckboxes().filter(cb => cb.checked);
    if (checked.length === 0) return;

    // Clear local UI immediately + show spinner on every previously-selected pill
    this.querySelectorAll('.cfm-child.is-selected').forEach(c => {
      c.classList.add('is-loading');
      c.classList.remove('is-selected');
    });
    this.querySelectorAll('.cfm-layer-children').forEach(l =>
      this._updateLayerMeta(l));
    this.querySelectorAll('[data-cfm-family]').forEach(p =>
      this._updateFamilyPill(p.dataset.cfmFamily));
    this._updateAllPill();

    // Show skeleton loading
    this._showLoading();

    // Uncheck all + AJAX (with self-flag)
    this._isTogglingSelf = true;
    checked.forEach(cb => { cb.checked = false; });
    this._dispatchChange(checked[checked.length - 1]);
    setTimeout(() => { this._isTogglingSelf = false; }, 0);
  }

  // ============================================================
  // Local UI state updaters
  // ============================================================

  _updateLayerMeta(layer) {
    if (!layer) return;

    // Count actual child buttons rendered in the DOM — this is the source
    // of truth. Dataset `data-cfm-total-children` was unreliable (could be
    // stripped/mutated by theme scripts or rendering quirks on mobile).
    const allChildren = layer.querySelectorAll('.cfm-child');
    const total = allChildren.length;
    const selected = layer.querySelectorAll('.cfm-child.is-selected').length;

    const meta = layer.querySelector('[data-cfm-meta]');
    if (!meta) return;

    const label = meta.dataset.cfmShadesLabel || 'shades';

    meta.textContent = selected > 0
      ? `${selected} / ${total} ${label}`
      : `${total} ${label}`;
  }

  _updateFamilyPill(handle) {
    if (!handle) return;
    const pill = this.querySelector(
      `.cfm-pill[data-cfm-family="${CSS.escape(handle)}"]`
    );
    if (!pill) return;

    const layer = this.querySelector(
      `[data-cfm-family-layer="${CSS.escape(handle)}"]`
    );
    const selected = layer
      ? layer.querySelectorAll('.cfm-child.is-selected').length
      : 0;

    pill.classList.toggle('has-selection', selected > 0);

    const nameEl = pill.querySelector('[data-cfm-name]');
    if (nameEl) {
      const familyName = nameEl.dataset.cfmName || '';
      nameEl.innerHTML = selected > 0
        ? `${this._escapeHtml(familyName)} <em>(${selected})</em>`
        : this._escapeHtml(familyName);
    }
  }

  _updateAllPill() {
    const totalSelected = this.querySelectorAll('.cfm-child.is-selected').length;
    const allPill = this.querySelector('.cfm-pill-all');
    if (allPill) {
      allPill.classList.toggle('is-default', totalSelected === 0);
    }
  }

  _escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ============================================================
  // State sync from URL / form (for external changes)
  // ============================================================

  /** Sync from URL params — used on initial load + popstate + URL polling. */
  _syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const activeValues = new Set(params.getAll(FILTER_NAME));

    this._applySelectedSet(activeValues);
  }

  /** Sync from the live form's checkbox states — used when an external
   *  change event fires (chip removal, native toggle, etc.). */
  _syncFromForm() {
    const form = this.getForm();
    if (!form) {
      this._syncFromUrl();
      return;
    }
    const checkedValues = new Set(
      Array.from(form.querySelectorAll(
        `input[type="checkbox"][name="${FILTER_NAME}"]:checked`
      )).map(cb => cb.value)
    );
    this._applySelectedSet(checkedValues);
  }

  _applySelectedSet(activeValues) {
    this.querySelectorAll('.cfm-child').forEach((btn) => {
      const value = btn.dataset.cfmValue;
      btn.classList.toggle('is-selected', activeValues.has(value));
    });

    this.querySelectorAll('.cfm-layer-children').forEach(l =>
      this._updateLayerMeta(l));
    this.querySelectorAll('[data-cfm-family]').forEach(p =>
      this._updateFamilyPill(p.dataset.cfmFamily));
    this._updateAllPill();
  }

  // ============================================================
  // Trigger theme's AJAX
  // ============================================================

  _dispatchChange(input) {
    if (!input) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('color-family-bar')) {
  customElements.define('color-family-bar', ColorFamilyBar);
}